import { useState, useEffect, useCallback } from 'react';
import { Sidebar } from '../layout/Sidebar';
import { TopBar } from '../layout/TopBar';
import { FilterPane } from '../shared/FilterPane';
import { useRestaurant } from '../../contexts/RestaurantContext';
import { useAuth } from '../../contexts/AuthContext';
import { useCurrency } from '../../hooks/useCurrency';
import { usePageTitle } from '../../hooks/usePageTitle';
import { customersService, Customer, CustomerTransaction } from '../../services/customers.service';
import { RevenueChart } from './RevenueChart';
import { RevenueDataPoint } from '../../services/dashboard.service';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer, Cell,
} from 'recharts';
import {
  Users2, DollarSign, CreditCard, TrendingUp, ArrowRight, Loader2,
  Receipt, Star, UserCheck, RefreshCw, Building2, MapPin,
} from 'lucide-react';
import { Badge } from '../ui/badge';

interface CustomersDashboardProps {
  onNavigate?: (page: string) => void;
}

interface TxStats {
  totalTransactions: number;
  totalRevenue: number;
  totalCustomers: number;
  activeCustomers: number;
  avgTransactionValue: number;
  revenueChange: number;
}

const TIER_CONFIG: Record<string, { label: string; color: string }> = {
  BRONZE:   { label: 'Bronze',   color: 'bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-400' },
  SILVER:   { label: 'Silver',   color: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300' },
  GOLD:     { label: 'Gold',     color: 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400' },
  PLATINUM: { label: 'Platinum', color: 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-400' },
};

const TX_TYPE: Record<string, string> = {
  PAYMENT: 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400',
  REFUND:  'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400',
  CREDIT:  'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400',
  CHARGE:  'bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-400',
};

function timeAgo(dateStr: string): string {
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diffMs / 60000);
  const hours = Math.floor(mins / 60);
  const days = Math.floor(hours / 24);
  if (mins < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function CustomersDashboard({ onNavigate }: CustomersDashboardProps) {
  usePageTitle('Dashboard');

  const [sidebarOpen, setSidebarOpen] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth >= 768 : true
  );
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<TxStats | null>(null);
  const [recentTransactions, setRecentTransactions] = useState<(CustomerTransaction & { customerName: string })[]>([]);
  const [topCustomers, setTopCustomers] = useState<(Customer & { periodSpent: number })[]>([]);
  const [revenueData, setRevenueData] = useState<RevenueDataPoint[]>([]);
  const [customerTypeData, setCustomerTypeData] = useState<{ type: string; count: number; revenue: number }[]>([]);
  const [dateRange, setDateRange] = useState<{ start: Date; end: Date }>(() => {
    const end = new Date();
    const start = new Date(end.getTime() - 30 * 24 * 3600 * 1000);
    return { start, end };
  });

  const { currentBranch, restaurantName } = useRestaurant();
  const { user } = useAuth();
  const { formatCurrency } = useCurrency();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [customersRes, statsRes] = await Promise.all([
        customersService.getCustomers({ page: 1, limit: 200 }),
        customersService.getCustomerStats(),
      ]);

      const customers: Customer[] = (() => {
        const p: any = customersRes.data;
        if (Array.isArray(p)) return p;
        if (Array.isArray(p?.data)) return p.data;
        if (Array.isArray(p?.data?.data)) return p.data.data;
        return [];
      })();

      // Fetch all transactions for recent activity and revenue build-up
      const txResults = await Promise.all(
        customers.slice(0, 50).map((c) =>
          customersService.getTransactions(c.id).then((r) => ({
            customerId: c.id,
            customerName: `${c.firstName} ${c.lastName}`,
            transactions: r.success && r.data ? r.data : [],
          })).catch(() => ({ customerId: c.id, customerName: '', transactions: [] }))
        )
      );

      // Flatten & tag transactions with customerId
      const allTx = txResults.flatMap((r) =>
        r.transactions.map((tx) => ({ ...tx, customerName: r.customerName, customerId: r.customerId }))
      );

      // Filter transactions to the selected date range
      const rangeStart = dateRange.start.getTime();
      const rangeEnd = dateRange.end.getTime();
      const filteredTx = allTx.filter((t) => {
        const ts = new Date(t.transactionDate).getTime();
        return ts >= rangeStart && ts <= rangeEnd;
      });

      // Stats based on filtered range
      const payments = filteredTx.filter((t) => t.type === 'PAYMENT');
      const totalRevenue = payments.reduce((s, t) => s + Number(t.amount), 0);
      const avgValue = payments.length ? totalRevenue / payments.length : 0;

      // Period-over-period revenue change (same duration immediately before)
      const duration = Math.max(rangeEnd - rangeStart, 1);
      const priorRevenue = allTx
        .filter((t) => {
          const ts = new Date(t.transactionDate).getTime();
          return t.type === 'PAYMENT' && ts >= rangeStart - duration && ts < rangeStart;
        })
        .reduce((s, t) => s + Number(t.amount), 0);
      const revenueChange = priorRevenue > 0 ? ((totalRevenue - priorRevenue) / priorRevenue) * 100 : 0;

      setStats({
        totalTransactions: filteredTx.length,
        totalRevenue,
        totalCustomers: statsRes.data?.totalCustomers ?? customers.length,
        activeCustomers: statsRes.data?.activeCustomers ?? customers.filter((c) => c.isActive).length,
        avgTransactionValue: avgValue,
        revenueChange,
      });

      // Recent 5 transactions in range sorted by date
      const sorted = [...filteredTx].sort(
        (a, b) => new Date(b.transactionDate).getTime() - new Date(a.transactionDate).getTime()
      );
      setRecentTransactions(sorted.slice(0, 5) as any);

      // Customer type: count all-time (structural), revenue for filtered period
      const typeMap: Record<string, { count: number; revenue: number }> = {};
      customers.forEach((c) => {
        const typeName = c.customerType?.name ?? 'General';
        if (!typeMap[typeName]) typeMap[typeName] = { count: 0, revenue: 0 };
        typeMap[typeName].count += 1;
      });
      payments.forEach((t) => {
        const cust = customers.find((c) => c.id === (t as any).customerId);
        const typeName = cust?.customerType?.name ?? 'General';
        if (typeMap[typeName]) typeMap[typeName].revenue += Number(t.amount);
      });
      setCustomerTypeData(
        Object.entries(typeMap)
          .map(([type, v]) => ({ type, ...v }))
          .sort((a, b) => b.count - a.count)
      );

      // Top 5 customers by spend in the filtered period
      const spendMap: Record<string, number> = {};
      payments.forEach((t) => {
        const id = (t as any).customerId;
        spendMap[id] = (spendMap[id] || 0) + Number(t.amount);
      });
      const top = customers
        .filter((c) => spendMap[c.id] !== undefined)
        .sort((a, b) => (spendMap[b.id] || 0) - (spendMap[a.id] || 0))
        .slice(0, 5)
        .map((c) => ({ ...c, periodSpent: spendMap[c.id] || 0 }));
      setTopCustomers(top);

      // Build revenue chart spanning the full selected date range
      // Use UTC throughout so dayMap keys match toISOString() transaction keys in all timezones
      const dayMap: Record<string, { revenue: number; orders: number }> = {};
      const cursor = new Date(dateRange.start);
      cursor.setUTCHours(0, 0, 0, 0);
      const endDay = new Date(dateRange.end);
      endDay.setUTCHours(23, 59, 59, 999);
      while (cursor <= endDay) {
        dayMap[cursor.toISOString().slice(0, 10)] = { revenue: 0, orders: 0 };
        cursor.setUTCDate(cursor.getUTCDate() + 1);
      }
      payments.forEach((t) => {
        const key = new Date(t.transactionDate).toISOString().slice(0, 10);
        if (dayMap[key]) {
          dayMap[key].revenue += Number(t.amount);
          dayMap[key].orders += 1;
        }
      });
      setRevenueData(
        Object.entries(dayMap).map(([date, v]) => ({ date, ...v }))
      );
    } catch (e) {
      console.error('CustomersDashboard load error', e);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => { load(); }, [load]);

  const handleDateRangeChange = (startDate: Date, endDate: Date) => {
    setDateRange({ start: startDate, end: endDate });
  };

  const formatCompact = (n: number) => {
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(0)}k`;
    return n.toFixed(0);
  };

  const statCards = [
    {
      icon: CreditCard,
      label: 'Total Transactions',
      value: stats?.totalTransactions.toString() ?? '0',
      sub: `${Math.abs(stats?.revenueChange ?? 0).toFixed(1)}% vs prior period`,

      positive: (stats?.revenueChange ?? 0) >= 0,
      color: 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400',
      navigate: 'customers',
    },
    {
      icon: DollarSign,
      label: 'Total Revenue',
      value: formatCompact(stats?.totalRevenue ?? 0),
      sub: `${Math.abs(stats?.revenueChange ?? 0).toFixed(1)}% vs prior period`,

      positive: (stats?.revenueChange ?? 0) >= 0,
      color: 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400',
      navigate: null,
    },
    {
      icon: TrendingUp,
      label: 'Avg. Transaction',
      value: formatCompact(stats?.avgTransactionValue ?? 0),
      sub: 'Per payment',
      positive: true,
      color: 'bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400',
      navigate: null,
    },
    {
      icon: Users2,
      label: 'Total Customers',
      value: stats?.totalCustomers.toString() ?? '0',
      sub: `${stats?.activeCustomers ?? 0} active`,
      positive: true,
      color: 'bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
      navigate: 'customers',
    },
  ];

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-950">
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        activePage="dashboard"
        onNavigate={onNavigate}
        ordersCount={0}
      />

      <div className={`flex-1 flex flex-col overflow-hidden transition-all duration-300 ${sidebarOpen ? 'min-[768px]:ml-64' : 'ml-0'}`}>
        <TopBar onMenuClick={() => setSidebarOpen(!sidebarOpen)} sidebarOpen={sidebarOpen} onNavigate={onNavigate} />

        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-[1400px] mx-auto">

            {/* Header */}
            <div className="mb-6 flex items-start justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Dashboard</h1>
                <p className="text-gray-400 dark:text-gray-500 mt-1">Welcome back, {restaurantName}!</p>
                {currentBranch && (
                  <div className="mt-3 flex items-center gap-2 flex-wrap">
                    <div className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border border-indigo-100 dark:border-indigo-800">
                      <Building2 className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                      <span className="text-sm font-medium text-indigo-700 dark:text-indigo-300">{currentBranch.name}</span>
                      {currentBranch.isMain && <Badge variant="secondary" className="ml-1.5 text-xs h-5 px-1.5">Main</Badge>}
                    </div>
                    {currentBranch.address && (
                      <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700">
                        <MapPin className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                        <span className="text-sm text-gray-600 dark:text-gray-400">{currentBranch.address}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={load}
                  disabled={loading}
                  className="p-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50"
                  title="Refresh"
                >
                  <RefreshCw className={`w-5 h-5 text-gray-600 dark:text-gray-400 ${loading ? 'animate-spin' : ''}`} />
                </button>
                <FilterPane onDateRangeChange={handleDateRangeChange} />
              </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {statCards.map((card, i) => (
                <button
                  key={i}
                  onClick={() => card.navigate && onNavigate?.(card.navigate)}
                  disabled={loading || !card.navigate}
                  className="bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-800 text-left transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 disabled:cursor-default"
                >
                  {loading ? (
                    <div className="space-y-2">
                      <div className="h-8 w-16 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                      <div className="h-4 w-24 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                    </div>
                  ) : (
                    <div className="flex items-start gap-4">
                      <div className={`p-3 rounded-xl flex-shrink-0 ${card.color}`}>
                        <card.icon className="w-6 h-6" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-2xl font-bold text-gray-900 dark:text-white">{card.value}</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{card.label}</p>
                        <p className={`text-xs mt-1 font-medium ${card.positive ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'}`}>
                          {card.positive ? '↑' : '↓'} {card.sub}
                        </p>
                      </div>
                    </div>
                  )}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Recent Transactions */}
              <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-6 flex flex-col" style={{ height: '380px' }}>
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 p-2 rounded-xl">
                      <Receipt className="w-5 h-5" />
                    </div>
                    <div>
                      <h2 className="text-base font-semibold text-gray-900 dark:text-white">Recent Transactions</h2>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Latest payment activity</p>
                    </div>
                  </div>
                  <button
                    onClick={() => onNavigate?.('customers')}
                    className="flex items-center gap-1 text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300"
                  >
                    View all <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>

                {loading ? (
                  <div className="flex items-center justify-center flex-1">
                    <Loader2 className="w-6 h-6 text-indigo-500 animate-spin" />
                  </div>
                ) : recentTransactions.length === 0 ? (
                  <div className="flex flex-col items-center justify-center flex-1 text-center">
                    <Receipt className="w-8 h-8 text-gray-300 dark:text-gray-600 mb-2" />
                    <p className="text-sm text-gray-500 dark:text-gray-400">No transactions yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {recentTransactions.map((tx) => (
                      <div key={tx.id} className="flex items-center justify-between py-2 border-b border-gray-50 dark:border-gray-800 last:border-0">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center flex-shrink-0">
                            <span className="text-xs font-bold text-white">
                              {(tx as any).customerName?.split(' ').map((n: string) => n[0]).join('').slice(0, 2) || '?'}
                            </span>
                          </div>
                          <div className="min-w-0">
                            <p className="text-xs font-medium text-gray-900 dark:text-white truncate">{(tx as any).customerName}</p>
                            <p className="text-xs text-gray-400 dark:text-gray-500 truncate">{tx.description}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0 ml-2">
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${TX_TYPE[tx.type] || TX_TYPE.PAYMENT}`}>
                            {tx.type}
                          </span>
                          <div className="text-right">
                            <p className="text-xs font-semibold text-gray-900 dark:text-white">{formatCurrency(Number(tx.amount))}</p>
                            <p className="text-xs text-gray-400 dark:text-gray-500">{timeAgo(tx.transactionDate.toString())}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Revenue Chart */}
              <RevenueChart data={revenueData} loading={loading} ordersLabel="Transactions" />
            </div>

            {/* Customer type charts — only when 2+ types exist */}
            {!loading && customerTypeData.length > 1 && (
              <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Customer Count by Type */}
                <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 p-2 rounded-xl">
                      <Users2 className="w-5 h-5" />
                    </div>
                    <div>
                      <h2 className="text-base font-semibold text-gray-900 dark:text-white">Customer Count by Type</h2>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Number of customers per account type</p>
                    </div>
                  </div>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={customerTypeData} barCategoryGap="30%">
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(0,0,0,0.06)" />
                      <XAxis dataKey="type" tick={{ fontSize: 12 }} tickLine={false} axisLine={false} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 12 }} tickLine={false} axisLine={false} width={36} />
                      <RechartsTooltip
                        formatter={(v: number) => [v, 'Customers']}
                        contentStyle={{ borderRadius: 8, fontSize: 12, border: '1px solid rgba(0,0,0,0.08)' }}
                      />
                      <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                        {customerTypeData.map((_, i) => (
                          <Cell key={i} fill={['#6366f1', '#a855f7', '#22c55e', '#f59e0b', '#ec4899'][i % 5]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Revenue by Type */}
                <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 p-2 rounded-xl">
                      <DollarSign className="w-5 h-5" />
                    </div>
                    <div>
                      <h2 className="text-base font-semibold text-gray-900 dark:text-white">Revenue by Type</h2>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Total spend per account type</p>
                    </div>
                  </div>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={customerTypeData} barCategoryGap="30%">
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(0,0,0,0.06)" />
                      <XAxis dataKey="type" tick={{ fontSize: 12 }} tickLine={false} axisLine={false} />
                      <YAxis tick={{ fontSize: 12 }} tickLine={false} axisLine={false} width={52}
                        tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : String(v)} />
                      <RechartsTooltip
                        formatter={(v: number) => [formatCurrency(v), 'Revenue']}
                        contentStyle={{ borderRadius: 8, fontSize: 12, border: '1px solid rgba(0,0,0,0.08)' }}
                      />
                      <Bar dataKey="revenue" radius={[4, 4, 0, 0]}>
                        {customerTypeData.map((_, i) => (
                          <Cell key={i} fill={['#22c55e', '#6366f1', '#a855f7', '#f59e0b', '#ec4899'][i % 5]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* Top Customers */}
            <div className="mt-6 bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="bg-yellow-50 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400 p-2 rounded-xl">
                    <Star className="w-5 h-5" />
                  </div>
                  <div>
                    <h2 className="text-base font-semibold text-gray-900 dark:text-white">Top Customers</h2>
                    <p className="text-xs text-gray-500 dark:text-gray-400">By total spend</p>
                  </div>
                </div>
                <button
                  onClick={() => onNavigate?.('customers')}
                  className="flex items-center gap-1 text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300"
                >
                  All customers <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 text-indigo-500 animate-spin" />
                </div>
              ) : topCustomers.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-sm text-gray-500 dark:text-gray-400">No customers yet</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                  {topCustomers.map((c, rank) => {
                    const tier = TIER_CONFIG[c.membershipTier] || TIER_CONFIG.BRONZE;
                    const initials = `${c.firstName[0] || ''}${c.lastName[0] || ''}`.toUpperCase();
                    return (
                      <button
                        key={c.id}
                        onClick={() => onNavigate?.(`customer-details-${c.id}`)}
                        className="flex flex-col items-center p-4 rounded-xl border border-gray-100 dark:border-gray-800 hover:border-indigo-200 dark:hover:border-indigo-700 hover:shadow-md transition-all cursor-pointer text-center"
                      >
                        <div className="relative mb-3">
                          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center">
                            <span className="text-sm font-bold text-white">{initials}</span>
                          </div>
                          <span className="absolute -top-1 -left-1 w-5 h-5 rounded-full bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-xs font-bold flex items-center justify-center">
                            {rank + 1}
                          </span>
                        </div>
                        <p className="text-sm font-semibold text-gray-900 dark:text-white leading-tight">{c.firstName} {c.lastName}</p>
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 truncate w-full">{c.email}</p>
                        <span className={`mt-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${tier.color}`}>
                          {tier.label}
                        </span>
                        <p className="mt-2 text-sm font-bold text-gray-900 dark:text-white">
                          {formatCurrency(c.periodSpent)}
                        </p>
                        <div className="flex items-center gap-1 mt-1">
                          <UserCheck className="w-3 h-3 text-gray-400" />
                          <p className="text-xs text-gray-400 dark:text-gray-500">{c.loyaltyPoints} pts</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

          </div>
        </main>
      </div>
    </div>
  );
}
